import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Sparkles, ArrowRight, Calendar, Clock, MapPin, Music, PartyPopper, Mic } from "lucide-react";
import { api } from "../../lib/api";
import { EventCardGrid } from "../../components/EventCard";

// Background images for the hero slideshow. Files must live in /public.
const HERO_SLIDES = ["/s1.png", "/s2.png", "/s3.png", "/s4.png"];
const SLIDE_DURATION_MS = 5500;

export default function HomePage() {
    const { t } = useTranslation();
    const [top, setTop] = useState([]);
    const [upcoming, setUpcoming] = useState([]);
    const [past, setPast] = useState([]);
    const [loading, setLoading] = useState(true);
    const [slideIndex, setSlideIndex] = useState(0);

    useEffect(() => {
        (async () => {
            try {
                const [a, b, c] = await Promise.all([
                    api.get("/api/events/top"),
                    api.get("/api/events/upcoming"),
                    api.get("/api/events/past"),
                ]);
                setTop(a.data || []);
                setUpcoming(b.data || []);
                setPast(c.data || []);
            } catch (e) {
                console.error(e);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    useEffect(() => {
        if (HERO_SLIDES.length <= 1) return;
        const id = setInterval(() => {
            setSlideIndex((i) => (i + 1) % HERO_SLIDES.length);
        }, SLIDE_DURATION_MS);
        return () => clearInterval(id);
    }, []);

    const platforms = [
        { key: "concert", icon: Mic },
        { key: "festival", icon: PartyPopper },
        { key: "live_performance", icon: Music },
    ];

    return (
        <div data-testid="homepage">
            {/* HERO — full-bleed crossfade slideshow with overlaid content */}
            <section
                data-testid="hero-slideshow"
                className="relative overflow-hidden min-h-[560px] md:min-h-[640px] lg:min-h-[720px] flex items-center"
            >
                {/* Slideshow layers */}
                <div className="absolute inset-0" aria-hidden="true">
                    {HERO_SLIDES.map((src, i) => (
                        <div
                            key={src}
                            data-testid={`hero-slide-${i}`}
                            className="absolute inset-0 transition-opacity duration-[1500ms] ease-in-out"
                            style={{
                                opacity: i === slideIndex ? 1 : 0,
                                backgroundImage: `url("${src}")`,
                                backgroundSize: "cover",
                                backgroundPosition: "center",
                                // Slow ken-burns zoom on the active slide only
                                transform: i === slideIndex ? "scale(1.06)" : "scale(1)",
                                transition:
                                    "opacity 1500ms ease-in-out, transform 8000ms ease-out",
                            }}
                        />
                    ))}
                    {/* Dark overlay for text legibility */}
                    <div className="absolute inset-0 bg-black/55" />
                </div>

                {/* Content */}
                <div className="relative w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
                    <div className="max-w-4xl mx-auto text-center animate-fade-in-up">
                        {/* Badge */}
                        <div className="flex justify-center mb-8">
                            <div
                                data-testid="hero-badge"
                                className="inline-flex items-center gap-2 px-4 py-2 bg-white/15 backdrop-blur-md border border-white/25 rounded-full"
                            >
                                <Sparkles className="h-4 w-4 text-white" strokeWidth={2} />
                                <span className="text-sm font-bold text-white">
                                    {t("home.hero_badge")}
                                </span>
                            </div>
                        </div>

                        {/* Headline */}
                        <h1
                            data-testid="hero-title"
                            className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black leading-[1.1] mb-6 tracking-tight text-white drop-shadow-lg"
                        >
                            {t("home.hero_title")}
                        </h1>

                        <p className="text-lg md:text-xl text-white/85 mb-10 max-w-2xl mx-auto font-medium drop-shadow">
                            {t("home.hero_subtitle")}
                        </p>

                        {/* Platform badges */}
                        <div className="flex flex-wrap justify-center gap-3 mb-10">
                            {platforms.map(({ key, icon: Icon }) => (
                                <div
                                    key={key}
                                    data-testid={`hero-platform-${key}`}
                                    className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-md rounded-full border border-white/20 text-white"
                                >
                                    <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center">
                                        <Icon className="h-3 w-3 text-white" strokeWidth={2} />
                                    </div>
                                    <span className="text-sm font-bold">
                                        {t(`home.platforms.${key}`)}
                                    </span>
                                </div>
                            ))}
                        </div>

                        {/* CTA */}
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <Link
                                to="/search"
                                data-testid="hero-cta"
                                className="inline-flex items-center justify-center gap-2 rounded-full bg-white text-foreground px-8 py-5 text-base font-bold shadow-strong hover:shadow-xl hover:opacity-95 transition-all active:scale-[0.97]"
                            >
                                {t("home.hero_cta")}
                                <ArrowRight className="h-5 w-5 ml-1" strokeWidth={2} />
                            </Link>
                        </div>

                        {/* Slide indicators */}
                        {HERO_SLIDES.length > 1 && (
                            <div
                                data-testid="hero-slide-dots"
                                className="flex justify-center gap-2 mt-12"
                            >
                                {HERO_SLIDES.map((_, i) => (
                                    <button
                                        key={i}
                                        type="button"
                                        aria-label={`Slide ${i + 1}`}
                                        data-testid={`hero-slide-dot-${i}`}
                                        onClick={() => setSlideIndex(i)}
                                        className={`h-1.5 rounded-full transition-all ${
                                            i === slideIndex
                                                ? "w-8 bg-white"
                                                : "w-1.5 bg-white/40 hover:bg-white/70"
                                        }`}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </section>

            {loading ? (
                <div className="max-w-7xl mx-auto px-4 py-20 text-center text-muted-foreground">
                    {t("common.loading")}
                </div>
            ) : (
                <>
                    {/* TOP EVENTS */}
                    {top.length > 0 && (
                        <Section
                            testId="top-events-section"
                            title={t("home.top_events")}
                            icon={<Sparkles className="h-6 w-6" />}
                        >
                            <EventCardGrid events={top} />
                        </Section>
                    )}

                    {/* UPCOMING */}
                    <Section
                        testId="upcoming-events-section"
                        title={t("home.upcoming_events")}
                        icon={<Clock className="h-6 w-6" />}
                    >
                        {upcoming.length ? <EventCardGrid events={upcoming} /> : <EmptyState />}
                    </Section>

                    {/* PAST */}
                    {past.length > 0 && (
                        <Section
                            testId="past-events-section"
                            title={t("home.past_events")}
                            icon={<Calendar className="h-6 w-6" />}
                        >
                            <EventCardGrid events={past} />
                        </Section>
                    )}
                </>
            )}
        </div>
    );
}

const Section = ({ title, children, icon, testId }) => (
    <section data-testid={testId} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <div className="flex items-center gap-3 mb-8">
            <div className="p-2.5 rounded-xl bg-muted">{icon}</div>
            <h2 className="font-heading font-black text-3xl md:text-4xl tracking-tight">{title}</h2>
        </div>
        {children}
    </section>
);

const EmptyState = () => {
    const { t } = useTranslation();
    return (
        <div className="rounded-2xl border-2 border-dashed border-border p-12 text-center text-muted-foreground">
            <MapPin className="h-8 w-8 mx-auto mb-3 opacity-40" />
            <p className="font-medium">{t("home.no_events")}</p>
        </div>
    );
};
